/**
 * MusicKit JS helper
 * Dynamically loads Apple's MusicKit JS from CDN and provides typed access
 */

const MUSICKIT_CDN = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';

declare global {
  interface Window {
    MusicKit?: {
      // In v3, configure() is async and returns the configured instance
      configure(config: {
        developerToken: string;
        app: { name: string; build?: string };
      }): Promise<MusicKitInstance>;
      getInstance(): MusicKitInstance | undefined;
    };
  }
}

interface MusicKitInstance {
  authorize(): Promise<string>;
  unauthorize(): Promise<void>;
  isAuthorized: boolean;
  musicUserToken: string;
}

let loadPromise: Promise<void> | null = null;

/**
 * Loads MusicKit JS from Apple's CDN (idempotent — safe to call multiple times).
 * Waits for the 'musickitloaded' event which MusicKit v3 fires when fully ready.
 */
export function loadMusicKit(): Promise<void> {
  if (window.MusicKit) return Promise.resolve();

  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    // MusicKit v3 fires 'musickitloaded' when the library is fully initialized,
    // which may be slightly after the script's onload event.
    const onLoaded = () => {
      document.removeEventListener('musickitloaded', onLoaded);
      resolve();
    };
    document.addEventListener('musickitloaded', onLoaded);

    const script = document.createElement('script');
    script.src = MUSICKIT_CDN;
    script.onerror = () => {
      document.removeEventListener('musickitloaded', onLoaded);
      reject(new Error('Failed to load MusicKit JS'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Initialize MusicKit with a developer token, then prompt user authorization.
 * Returns the Music User Token on success.
 */
export async function authorizeMusicKit(developerToken: string): Promise<string> {
  await loadMusicKit();

  if (!window.MusicKit) {
    throw new Error('MusicKit JS failed to load');
  }

  // In MusicKit v3, configure() is async and returns the configured instance directly.
  // Do NOT call getInstance() after configure() — it may return undefined.
  const instance = await window.MusicKit.configure({
    developerToken,
    app: { name: 'Constellations' },
  });

  const musicUserToken = await instance.authorize();
  return musicUserToken;
}
